import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMappedFields, getUploadLogData, loadPageData, resetUploadForm, runEtlProcess, setErrors, setFieldMappingTimeoutId, setStepVal, updateMetadata } from '../../../store/uploads/actions';
import FileUploadSection from './step2/FileUploadSection';
import FormFieldsSection from './step1/FormFieldsSection';
import SubmitSection from './step3/SubmitSection';
import constants from '../../../commonUtils/constants';
import { Card, CardContent, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import StepsNav from './steps/StepsNav';
import Loader from '../../../layout/Loader';
import { getModuleName, isValidObjectId } from '../../../commonUtils/string.helper';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { toast } from 'react-toastify';
import { loadClusterState } from '../../../store/cluster/actions';
import ClusterModule from '../../../layout/Cluster/ClusterModule';

const UploadFileSection = () => {
    // Add ref to track the timeout ID
    const timeoutRef = useRef(null);
    
    const { key } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const {
        pageLoadData,
        stepVal,
        uploadData,
        uploadFile,
        mappedColumns,
        selectedColumns,
        destinationColumns,
        loading,
        fieldMappingTimeoutId,
        errors, // Add errors to destructuring
    } = useSelector((state) => state.Uploads);
    const { user } = useSelector((state) => state.Auth);

    const [navBtnsDisabled, setNavBtnsDisabled] = useState(loading);
    const [confirmAwaitFieldMapping, setConfirmAwaitFieldMapping] = useState(false);
    const [showClusterTooltip, setShowClusterTooltip] = useState(false);
    const selectedCols = useMemo(() => selectedColumns.reduce((acc, curr) => {
            acc[curr["Input_Column"]] = { [curr["Mapped_Column"]]: curr["Mapped_DataType"].toLowerCase() };
            return acc;
        }, {})
    , [selectedColumns]);

    // Keep reset logic stable so effects can safely depend on it.
    const resetForm = React.useCallback(() => {
        dispatch(resetUploadForm(user));
        dispatch(setErrors(constants.upload.errorInitialState));
    }, [dispatch, user]);

    useEffect(() => {
        if (!key) {
            resetForm();
        }
    }, [key, resetForm]);

    useEffect(() => {
        if (stepVal === 1) {
            dispatch(loadPageData());
        }
    }, [dispatch, stepVal]);

    useEffect(() => {
        if (key && !uploadData.id) {
            dispatch(getUploadLogData({ key, navigate }));
        }
    }, [dispatch, key, navigate, uploadData.id]);
    // Add effect to clear timeout when stepVal changes
    useEffect(() => {
        // Clear the timeout when stepVal changes
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if(stepVal === 1) {
            setNavBtnsDisabled(false);
        }
    }, [stepVal]);
    // Cleanup on unmount
    useEffect(() => {
        // Cleanup timeout when component unmounts or timeout changes
        // if (fieldMappingTimeoutId) {
        //     clearTimeout(fieldMappingTimeoutId);
        // }
        return () => {
            if (fieldMappingTimeoutId) {
                clearTimeout(fieldMappingTimeoutId);
            }
        };
    }, [fieldMappingTimeoutId]);
    useEffect(() => {
        dispatch(loadClusterState());
        let hideTimer;
        const showTimer = setTimeout(() => {
            setShowClusterTooltip(true);
            hideTimer = setTimeout(() => {
            setShowClusterTooltip(false);
            }, 5000); // hide after 5 seconds
            // Optional: clear hideTimer on cleanup
        }, 4000); // show after 4 seconds
        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, [dispatch]);
    
    const validateRequiredFields = () => {
        const ers = {};
        const ersSelectedColumns = selectedColumns.map(col => ({
            source: "",
            sourceType: "",
            destination: "",
            destType: "",
        }));
        if(stepVal < 3){
            let requiredFields = ["sourceType", "masterProject", "project", "spoc", "uploadedBy", "dataCategory", "leadSource"];
            if(uploadData.sourceType === "Channel Partner") {
                requiredFields.indexOf("vendor") !== -1 && requiredFields.splice(requiredFields.indexOf("vendor"), 1);
                requiredFields.push("channelPartner")
            } else {
                requiredFields.indexOf("channelPartner") !== -1 && requiredFields.splice(requiredFields.indexOf("channelPartner"), 1);
                requiredFields.push("vendor")
            }
            requiredFields.forEach(field => {
                if (!uploadData[field] || (Array.isArray(uploadData[field]) && uploadData[field].length === 0)) {
                    ers[field] = `${getModuleName(field)} is required`;
                }
            });
            
            // FIX 4: Improved validation logic - only validate for custom entries if they don't already have errors from SelectInput
            if(uploadData.vendor && typeof uploadData.vendor === "string" && !isValidObjectId(uploadData.vendor)) {
                // Only add validation error if SelectInput hasn't already set one
                if (!errors.vendor) {
                    const exists = pageLoadData.vendors.some(({value, label}) => 
                        label.toLowerCase() === uploadData.vendor.toLowerCase()
                    );
                    if(exists) ers["vendor"] = `Vendor already exists`;
                }
            }
            
            if (uploadData.channelPartner && typeof uploadData.channelPartner === "string" && !isValidObjectId(uploadData.channelPartner)) {
                // Only add validation error if SelectInput hasn't already set one
                if (!errors.channelPartner) {
                    const exists = pageLoadData.channelPartners.some(({value, label}) => 
                        label.toLowerCase() === uploadData.channelPartner.toLowerCase()
                    );
                    if(exists) ers["channelPartner"] = `Channel Partner already exists`;
                }
            }
            
            if(stepVal > 1){
                if (!uploadFile) {
                    ers["file"] = `File is required`;
                    toast.error("Please upload your file before proceeding.");                    
                }
            }
        } else {
            if(Object.keys(selectedCols).length > 0) {
                ers.selectedColumns = JSON.parse(JSON.stringify(ersSelectedColumns))
            }
            selectedColumns.forEach((col, i) => {
                if (Object.keys(selectedCols).includes(col['Input_Column'])) {
                    if (col['Mapped_DataType'] === "") {
                        ers.selectedColumns[i].destType = "Data Type is required";
                    }
                    if (col['Mapped_Column'] === "") {
                        ers.selectedColumns[i].destination = "This field is required";
                    } else if (Object.keys(destinationColumns).includes(col['Mapped_Column'])) {
                        ers.selectedColumns[i].destination = "This column already exists";
                    } 
                    else if (/[^a-zA-Z0-9_]/.test(col['Mapped_Column'])) {
                        ers.selectedColumns[i].destination = "Special characters are not allowed";
                    }
                }
            });
        }
        if (JSON.stringify(ers.selectedColumns) === JSON.stringify(ersSelectedColumns))
            delete ers.selectedColumns;
            
        // FIX 5: Merge existing errors from SelectInput with new validation errors
        const finalErrors = { ...constants.upload.errorInitialState, ...errors, ...ers };
        
        if(Object.keys(ers).length > 0){
            dispatch(setErrors(finalErrors));
            return false;
        }
        
        // Check if there are any existing errors from SelectInput components
        const hasSelectInputErrors = errors.vendor || errors.channelPartner;
        if (hasSelectInputErrors) {
            return false;
        }
        
        return true;
    };
    
    const handleNextStep = () => {
        if (stepVal < 3) {
            const isValid = validateRequiredFields()
            // console.log("isValid", isValid)
            if(isValid) {
                if(stepVal === 2) {
                    if (["uploaded", "file_changed", "metadata_updated"].includes(uploadData.status)) {
                        dispatch(getMappedFields({id: uploadData._id || uploadData.id, navigate}))
                        // Store the timeout ID in the ref
                        const timeoutId = setTimeout(() => {
                            setConfirmAwaitFieldMapping(true);
                        }, constants.upload.fieldMappingTimeout);
                        dispatch(setFieldMappingTimeoutId(timeoutId));
                    } else if(!uploadData.id && !uploadData._id) {
                        toast.error("Please upload your file before proceeding.")
                    } else if(!uploadData?.fieldMapping?.status === "failed") {
                        toast.error("Field Mapping for the uploaded file failed.")
                        navigate("/upload-history");
                    } else dispatch(setStepVal(stepVal + 1));
                } else if(stepVal === 1 && uploadFile) {
                    dispatch(updateMetadata({ id: uploadData.id || uploadData._id, formData: uploadData }));
                } else {
                    dispatch(setStepVal(stepVal + 1));
                }
            }
        }
    };
    const handlePrevStep = () => {
        if (stepVal > 1) dispatch(setStepVal(stepVal - 1));
    };
    
    const handleSubmitFiles = () => {
        const isValid = validateRequiredFields();
        if(isValid) {
            const mappedCols = mappedColumns ? mappedColumns
            .filter(col => 
                col["Mapped_Column"] !== constants.upload.matchNotFound && col["Mapped_Column"] !== ""
            ).reduce((acc, curr) => {
                acc[curr["Input_Column"]] = curr["Mapped_Column"];
                return acc;
            }, {}) : [];
            // console.log("mappedCols", mappedCols)
            // console.log("selectedCols", selectedCols)
            dispatch(runEtlProcess({
                id: uploadData.id || uploadData._id,
                mappedColumns: JSON.stringify(mappedCols),
                additionalColumns: JSON.stringify(selectedCols),
                navigate
            }));
        }
    };
    
    const HeadingSection = ({title}) => (
        <div className="flex justify-between items-center">
            <h5 className="font-semibold text-md text-primary-300 mt-2">{title}</h5>
            <Button variant="border_outline" onClick={() => navigate('/upload-history')}>
                Show History
            </Button>
        </div>
    );
    
    return (
        <>
            <div className="flex justify-between items-center mb-2">
                <StepsNav stepVal={stepVal} />
                <ClusterModule showTooltip={showClusterTooltip} />
            </div>
            <Card>
                <CardTitle>{constants.upload.titles[stepVal]}</CardTitle>
                <CardContent className="flex flex-col gap-4">
                    <>
                        {stepVal === 1 && (
                            loading ? (
                                <div> <Loader /> </div>
                            ) : (
                                <>
                                    <HeadingSection title="Please provide details for form upload" />
                                    <FormFieldsSection />
                                </>
                            )
                        )}
                        {stepVal === 2 && (
                            <>
                                <HeadingSection title="Please upload the data file here" />
                                <FileUploadSection navBtnsDisabled={navBtnsDisabled} setNavBtnsDisabled={setNavBtnsDisabled} />
                            </>
                        )}
                        {stepVal === 3 && (
                            loading ? (
                                <div> <Loader /> </div>
                            ) : (
                                <>
                                    <HeadingSection title="Please map the fields form CSV to Existing Data Fields" />
                                    <SubmitSection />
                                </>
                            )
                        )}
                        <div className="flex justify-end mt-3">
                            {stepVal === 1 ? <Button variant="outline" onClick={resetForm} className="mr-2">
                                Reset
                            </Button> : <Button variant="outline" onClick={handlePrevStep} disabled={navBtnsDisabled} className="mr-2">
                                Previous
                            </Button>}
                            {stepVal < 3 ? (
                                <Button onClick={handleNextStep} className="mr-2" disabled={navBtnsDisabled} >
                                    Next
                                </Button>
                            ) : (
                                <Button onClick={handleSubmitFiles} className="mr-2">
                                    Submit
                                </Button>
                            )}
                        </div>
                    </>
                </CardContent>
            </Card>
            {/* <DownloadTemplate open={templateModalOpen} handleClose={handleCloseUpdateModal} /> */}
            <ConfirmDialog
                open={confirmAwaitFieldMapping}
                titleText="Almost There!"
                descriptionText="Field mapping is taking longer than expected. Please come back after sometime."
                actionText="OK"
                cancelText="I'll Wait"
                handleClose={() => setConfirmAwaitFieldMapping(false)}
                confirmAction={() => {
                    setConfirmAwaitFieldMapping(false);
                    navigate("/upload-history");
                }}
            />
        </>
    );
};
export default UploadFileSection;